import {
	DEFAULT_REWARD_CURRENCY,
	getRewardCurrency,
	REWARD_CURRENCIES,
} from "@/src/constants/rewardly-currencies";
import {
	ConfigureState,
	useConfigure,
} from "@/src/hooks/features/rewardly/use-configure";
import { useRewardCurrencyRates } from "@/src/hooks/features/rewardly/use-reward-rates";
import { useTheme } from "@/src/styles/theme-provider";
import { ArrangeHorizontal, ArrowLeft2, Gift } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	ActivityIndicator,
	Pressable,
	Text,
	TextInput,
	View,
} from "react-native";
import ConfirmModal from "../../action-modals/confirm-modal";
import ScButton from "../../ui-kit/button";
import ScDropdown from "../../ui-kit/dropdown-select";
import ScInput from "../../ui-kit/input";
import ActiveSeasonCard from "../season/active-season";

const CURRENCY_OPTIONS = REWARD_CURRENCIES.map((c) => ({
  label: `${c.flag}  ${c.code}`,
  value: c.code,
}));

const ConfigField = ({
  value,
  onChangeText,
  placeholderKey,
  editable = true,
  numeric = true,
}: Readonly<{
  value: string;
  onChangeText?: (text: string) => void;
  placeholderKey: string;
  editable?: boolean;
  numeric?: boolean;
}>) => {
  "use no memo";
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      editable={editable}
      keyboardType={numeric ? "number-pad" : "default"}
      placeholder={t(placeholderKey)}
      placeholderTextColor={themeVars["--color-slate-default"] as string}
      className={`flex-1 min-w-0 bg-ash md:border md:border-ash-3 rounded-xl px-3 py-3 text-sm focus:border-primary ${editable ? "text-onyx" : "text-slate opacity-70"}`}
      style={{ outlineStyle: "none" } as any}
    />
  );
};

const StaticBox = ({ label }: { label: string }) => {
  const { t } = useTranslation();
  return (
    <View className="bg-ash md:border md:border-ash-3 rounded-xl px-4 py-3 justify-center">
      <Text numberOfLines={1} className="text-sm text-slate">
        {t(label)}
      </Text>
    </View>
  );
};

const FieldLabel = ({ label, hint }: { label?: string; hint?: string }) => {
  const { t } = useTranslation();
  return (
    <View className="gap-0.5">
      {label && (
        <Text className="text-sm font-medium text-onyx">{t(label)}</Text>
      )}
      {hint && <Text className="text-xs text-ash-2">{t(hint)}</Text>}
    </View>
  );
};

/**
 * The editable season settings (Rewards | Star Allocation). Shared by the
 * create flow and the inline edit flow so both stay identical.
 */
const SeasonConfigFields = ({ configure }: { configure: ConfigureState }) => {
  const { themeVars } = useTheme();
  // Reward currency drives the displayed per-star value. Display-only for now —
  // the season schema has no currency/monetary-value field to persist.
  const [currencyCode, setCurrencyCode] = useState(DEFAULT_REWARD_CURRENCY);
  const currency = getRewardCurrency(currencyCode);
  // 1 star = 1 USD, so the per-star value is the live USD→currency rate
  // (refreshed daily). Falls back to the fixed rate until/if the fetch fails.
  const { getStarValue } = useRewardCurrencyRates();
  const starValue = getStarValue(currencyCode);

  return (
    <View className="gap-6">
      {/* Rewards | Star Allocation — two columns on web, stacked on mobile. */}
      <View className="flex-col gap-6 md:flex-row md:gap-10">
        <View className="md:flex-1 gap-5">
          <View className="gap-2.5">
            <FieldLabel label="LABEL_SET_STAR_EQUIVALENCE" />
            <View className="flex-row items-center gap-2">
              <StaticBox label="LABEL_ONE_STAR" />
              <ArrangeHorizontal
                size={16}
                color={themeVars["--color-slate-default"] as string}
              />
              {/* Auto-derived from the selected reward currency below, using
							    the live FX rate (1 star = 1 USD) with a fixed fallback. */}
              <ConfigField
                value={`${currency.symbol} ${starValue.toFixed(2)}`}
                placeholderKey="LABEL_SET_MONETARY_VALUE"
                editable={false}
                numeric={false}
              />
            </View>
          </View>

          <View className="gap-2.5">
            <FieldLabel label="LABEL_SET_STAR_QUANTITY" />
            <View className="flex-row items-center gap-2">
              <ConfigField
                value={configure.minQty}
                onChangeText={configure.setMinQty}
                placeholderKey="LABEL_SET_MIN_QUANTITY"
              />
              <ConfigField
                value={configure.maxQty}
                onChangeText={configure.setMaxQty}
                placeholderKey="LABEL_SET_MAX_QUANTITY"
              />
            </View>
          </View>
        </View>

        <View className="md:flex-1 gap-5">
          {/* Star Allocation, with the reward-currency picker below it.
					    Children use min-w-0 so they shrink (and text truncates)
					    instead of wrapping into extra height on narrow widths. */}
          <View className="gap-5">
            <View className="gap-2.5">
              <FieldLabel label="LABEL_STAR_ALLOCATION" />
              <View className="flex-row items-center gap-3">
                <View className="flex-1 min-w-0 max-w-[140px]">
                  <StaticBox label="LABEL_MEMBER" />
                </View>
                <ArrangeHorizontal
                  size={16}
                  color={themeVars["--color-slate-default"] as string}
                />
                <View className="flex-1 min-w-0 max-w-[160px]">
                  <ConfigField
                    value={configure.memberStars}
                    onChangeText={configure.setMemberStars}
                    placeholderKey="LABEL_SET_STARS"
                  />
                </View>
              </View>
            </View>

            <View className="gap-2.5">
              <FieldLabel label="LABEL_SET_REWARD_CURRENCY" />
              <View className="md:max-w-[240px]">
                <ScDropdown
                  options={CURRENCY_OPTIONS}
                  value={currencyCode}
                  onChange={setCurrencyCode}
                  triggerClassName="py-3 bg-ash"
                />
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

/**
 * Shown on the Configure tab when no season is active. Keeps the tab calm —
 * the create form only appears once the user opts in via the button.
 */
const EmptySeasonCard = ({ onCreate }: { onCreate: () => void }) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  return (
    <View className="bg-chalk rounded-xl md:rounded-2xl md:border md:border-ash-3 px-6 py-10 md:py-14 items-center gap-4">
      <View className="w-16 h-16 rounded-full bg-primary-1 items-center justify-center">
        <Gift
          size={28}
          color={themeVars["--color-primary-default"] as string}
        />
      </View>
      <View className="gap-1 items-center">
        <Text className="text-lg md:text-xl font-semibold text-onyx text-center">
          {t("LABEL_NO_ACTIVE_SEASON")}
        </Text>
        <Text className="text-sm text-ash-2 text-center max-w-[320px]">
          {t("LABEL_NO_SEASON_CONFIGURE_HINT")}
        </Text>
      </View>
      <ScButton text="BTN_CREATE_SEASON" onPress={onCreate} />
    </View>
  );
};

const ConfigureTab = () => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const configure = useConfigure();
  const [isEditing, setIsEditing] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  const hasActiveSeason = !!configure.activeSeason;

  if (configure.isLoading) {
    return (
      <View className="flex-1 justify-center items-center py-12">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  const handleConfirmEnd = async () => {
    const id = configure.activeSeason?.ItemId;
    if (!id) return;
    await configure.handleEndSeason(id);
    setShowEndConfirm(false);
  };

  if (hasActiveSeason && !isEditing) {
    return (
      <View className="flex-1 gap-6">
        <ActiveSeasonCard
          season={configure.activeSeason}
          onEndSeason={async () => setShowEndConfirm(true)}
          isEnding={configure.isUpdating}
          onEdit={() => setIsEditing(true)}
        />
        <ConfirmModal
          visible={showEndConfirm}
          title="QUEST_END_SEASON"
          description="HINT_END_SEASON"
          confirmText="BTN_END_SEASON"
          cancelText="BTN_CANCEL"
          loading={configure.isUpdating}
          onConfirm={handleConfirmEnd}
          onCancel={() => setShowEndConfirm(false)}
        />
      </View>
    );
  }

  // No active season and the create form hasn't been opened yet → empty card.
  if (!showCreateForm) {
    return (
      <View className="flex-1 gap-6">
        <EmptySeasonCard onCreate={() => setShowCreateForm(true)} />
      </View>
    );
  }

  const handleSave = async () => {
    const ok = await configure.handleSaveConfig();
    if (ok) setIsEditing(false);
  };

  const handleCancelEdit = () => {
    configure.resetForm();
    setIsEditing(false);
  };

  const handleCreate = async () => {
    const result = await configure.handleCreateSeason();
    if (result) setShowCreateForm(false);
  };

  const handleCancelCreate = () => {
    configure.setSeasonName("");
    configure.setMinQty("");
    configure.setMaxQty("");
    configure.setMemberStars("");
    setShowCreateForm(false);
  };

  return (
    <View className="flex-1 gap-6">
      {isEditing ? (
        <Pressable
          onPress={handleCancelEdit}
          className="flex-row items-center gap-2 self-start hover:bg-ash-3 hover:rounded-lg"
        >
          <ArrowLeft2
            size={18}
            color={themeVars["--color-onyx-default"] as string}
          />
          <Text className="text-base md:text-lg font-semibold text-onyx">
            {t("LABEL_EDIT_SEASON")}
          </Text>
        </Pressable>
      ) : (
        <View className="md:max-w-[420px]">
          <ScInput
            label="LABEL_SEASON_NAME"
            placeholder="LABEL_ENTER_SEASON_NAME"
            value={configure.seasonName}
            onChangeText={configure.setSeasonName}
          />
        </View>
      )}

      <SeasonConfigFields configure={configure} />

      <View className="flex-row justify-end gap-3 mt-2">
        <ScButton
          text="BTN_CANCEL"
          mode="outlined"
          onPress={isEditing ? handleCancelEdit : handleCancelCreate}
        />
        <ScButton
          text={isEditing ? "BTN_SAVE" : "BTN_CREATE_SEASON"}
          onPress={isEditing ? handleSave : handleCreate}
          loading={isEditing ? configure.isSavingConfig : configure.isCreating}
          disabled={isEditing ? false : !configure.isFormValid}
        />
      </View>
    </View>
  );
};

export default ConfigureTab;
